import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy, IReportVariantDef } from "@components/smart/smartTable";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { AccountActivityTypeCode, AccountTypeCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { isNonProfitCompany } from "@utils/CompanyUtils";
import i18next from "i18next";

import { getSimpleBoolSelectItems } from "../../../components/smart/FieldInfo";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { ConfigListItemBoundType, FieldType, Sort } from "../../../enums";
import { TRecordType } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import {
    assetIntentFormatter,
    CommonReportProps,
    documentNumberOursOverrideFormatterWithPaymentDocs,
    getDocumentTypeFilterDef
} from "../CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getComposedDateRangeFieldsDefs
} from "../customFilterComponents/ComposedDateRange";
import { getAccountsParamDef, IReportTableDefinition, TReportConfigListItemOverride } from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";
import { SaldoProps } from "../saldo/SaldoDef";

export const AccountAnalysisProps = {
    accounts: BindingContext.localContext("Accounts"),
    drilldown: BindingContext.localContext("DrillDown"),
    sumAmounts: BindingContext.localContext("SumAmounts"),
    closingAccounts: BindingContext.localContext("ClosingAccounts"),
    taxApplicability: BindingContext.localContext("TaxApplicability_Name"),
    accountType: BindingContext.localContext("AccountType_Name")
};

export enum ClosingAccounts {
    Show = "Show",
    DoNotShow = "DoNotShow"
}

export const DrillDownType = {
    "BsAssets": "BsAssets",
    "BsLiabilies": "BsLiabilies",
    "NpoShowFiscalYearFirstDay": "NpoShowFiscalYearFirstDay",
};

export type TDrillDownType = keyof typeof DrillDownType

export const AccountAnalysisConfiglistSettings: TRecordType<TReportConfigListItemOverride> = {
    JournalEntry_Amount: {
        isRequired: true,
        areItemsReadOnly: true,
        boundTo: ConfigListItemBoundType.Column
    }
};

export enum AccountAnalysisVariant {
    Evala = "-69", // default variant
    FYCBankDrilldown = "fycbank",    // FE only variant - used just in drilldown
}

export const getDefinition = (context: IAppContext): IReportTableDefinition => {
    const isNonProfit = isNonProfitCompany(context);

    const tableId = ReportId.AccountAnalysis;
    const title = i18next.t("Reporting:AccountAnalysis.Title");
    const path = "AccountAnalysis";
    const initialSortBy = [
        { id: "Account_Number", sort: Sort.Asc },
        { id: "JournalEntry_DateAccountingTransaction", sort: Sort.Desc },
        { id: "Document_NumberOurs", sort: Sort.Asc }
    ];


    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                CommonReportProps.dateRange,
                CommonReportProps.dateRangeCustomValue,
                AccountAnalysisProps.accounts,
                AccountAnalysisProps.drilldown,
                AccountAnalysisProps.sumAmounts,
                AccountAnalysisProps.closingAccounts
            ],
            filterDefinition: {
                ...getComposedDateRangeFieldsDefs(tableId),
                [AccountAnalysisProps.accounts]: getAccountsParamDef([], true),
                // hidden parameter only used for drilldown from BalanceSheet
                // not supposed to be seen in filter bar at all
                [AccountAnalysisProps.drilldown]: {
                    isVisible: false,
                    sendIfInvisible: true,
                    defaultValue: "None"
                },
                [SaldoProps.sumAmounts]: {
                    type: FieldType.ComboBox,
                    defaultValue: false,
                    label: i18next.t("Reporting:AccountAnalysis.SumAmounts"),
                    fieldSettings: {
                        items: getSimpleBoolSelectItems()
                    }
                },
                [AccountAnalysisProps.closingAccounts]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:AccountAnalysis.ClosingAccountsParam"),
                    defaultValue: "DoNotShow",
                    fieldSettings: {
                        items: [
                            {
                                id: ClosingAccounts.Show,
                                label: i18next.t("Reporting:AccountAnalysis.ShowClosingAccounts")
                            },
                            {
                                id: ClosingAccounts.DoNotShow,
                                label: i18next.t("Reporting:AccountAnalysis.DoNotShowClosingAccounts")
                            }
                        ]
                    }
                }
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {
                [BindingContext.localContext("DocumentType_Name")]: getDocumentTypeFilterDef("JournalEntry_DocumentTypeCode"),
                [BindingContext.localContext("AccountActivityType_Name")]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Reporting:Columns.AccountActivityType_Name"),
                    filterName: "JournalEntry_AccountActivityTypeCode",
                    defaultValue: AccountActivityTypeCode.Business,
                    fieldSettings: {
                        items: [
                            {
                                id: AccountActivityTypeCode.Main,
                                label: i18next.t("Reporting:AccountAnalysis.Main")
                            },
                            {
                                id: AccountActivityTypeCode.Business,
                                label: i18next.t("Reporting:AccountAnalysis.Business")
                            }
                        ]
                    }
                },
                [AccountAnalysisProps.accountType]: {
                    filterName: "Account_TypeCode",
                    formatter: (v) => getEnumDisplayValue(EntityTypeName.AccountType, v?.toString()),
                    fieldSettings: {
                        items: [
                            {
                                id: AccountTypeCode.Expense,
                                label: getEnumDisplayValue(EntityTypeName.AccountType, AccountTypeCode.Expense),
                            },
                            {
                                id: AccountTypeCode.Income,
                                label: getEnumDisplayValue(EntityTypeName.AccountType, AccountTypeCode.Income),
                            }
                        ]
                    }
                },
                [AccountAnalysisProps.taxApplicability]: {
                    filterName: "Account_TaxApplicabilityCode",
                    formatter: (v) => getEnumDisplayValue(EntityTypeName.TaxApplicability, v?.toString()),
                }
            }
        }
    ];
    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage, true);
    };
    const parameters: string[] = [
        CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue,
        AccountAnalysisProps.accounts,
        AccountAnalysisProps.sumAmounts,
        AccountAnalysisProps.drilldown, AccountAnalysisProps.closingAccounts
    ];

    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": false,
        "Groups": [],
        "Columns": [
            {
                "ColumnAlias": "Account_Number"
            },
            {
                "ColumnAlias": "PairAccount_Number"
            },
            {
                "ColumnAlias": "JournalEntry_DateAccountingTransaction"
            },
            {
                "ColumnAlias": "FiscalPeriod_Name"
            },
            {
                "ColumnAlias": "Document_NumberOurs"
            },
            {
                "ColumnAlias": "DocumentType_Name"
            },
            {
                "ColumnAlias": "JournalEntry_Description"
            },
            {
                "ColumnAlias": "JournalEntry_Debit"
            },
            {
                "ColumnAlias": "JournalEntry_Credit"
            }
        ],
        "Aggregations": []
    };

    const defaultReportVariants: TRecordType<IReportVariantDef> = {
        [AccountAnalysisVariant.Evala]: {
            ReportHierarchy: defaultReportHierarchy
        },
        [AccountAnalysisVariant.FYCBankDrilldown]: {
            ReportHierarchy: {
                "Aggregate": false,
                "Groups": [],
                "Columns": [
                    { "ColumnAlias": "Account_Number" },
                    { "ColumnAlias": "PairAccount_Number" },
                    { "ColumnAlias": "JournalEntry_DateAccountingTransaction" },
                    { "ColumnAlias": "FiscalPeriod_Name" },
                    { "ColumnAlias": "Document_NumberOurs" },
                    { "ColumnAlias": "DocumentType_Name" },
                    { "ColumnAlias": "JournalEntry_Description" },
                    { "ColumnAlias": "JournalEntry_Amount" },
                    { "ColumnAlias": "JournalEntry_TransactionCurrencyCode" },
                    { "ColumnAlias": "Document_ExchangeRatePerUnit" },
                    { "ColumnAlias": "JournalEntry_TransactionAmount" }
                ],
                "Aggregations": []
            }
        }
    };

    const columnOverrides = {
        Document_NumberOurs: {
            formatter: documentNumberOursOverrideFormatterWithPaymentDocs
        },
        Asset: {
            formatter: assetIntentFormatter
        }
    };

    return {
        title, path, id: tableId, initialSortBy, filterBarDef,
        onBeforeLoad, columnOverrides, parameters, defaultReportHierarchy, defaultReportVariants,
        configListItemsOverride: AccountAnalysisConfiglistSettings
    };
};

setReportDef(ReportId.AccountAnalysis, getDefinition);